import React, {useEffect, useMemo, useState, useRef} from "react";
import "./App.css";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import Root from "./pages/Root";
import ErrorPage from "./pages/error";
import { Header, HeaderV2 } from "./components/header";
import { Footer, FooterV2 } from "./components/footer";
import {
  ApplicationCOOStep1,
  StartApplicationCOO,
  ApplicationCOOStep2,
  ApplicationCOOStep3,
  ApplicationCOOStep4,
  ApplicationCOOStep5,
  StagesProcess,
} from "./pages/application-coo";
import { useAppSelector, useAppDispatch } from "./redux/hook";
import StageCPaymentLanding from "./pages/application-coo-stage-c/stage-c-payment-landing";
import { ConsentPage } from "./pages/application-coo-stage-b";
import OthersReview from "./pages/application-coo-stage-b/others-review";
import StageCSuccessful from "./pages/application-coo-stage-c/stage-c-success";
import StageCUnsuccessful from "./pages/application-coo-stage-c/stage-c-unsuccessful";
import StageDApplicationReview from "./pages/application-coo-stage-d/step-1/landing";
import { keys, reduce } from "lodash";
import PropertySelection from "./pages/application-coo/property-selection";
import config from "./config";
import StageDFinancialReview from "./pages/application-coo-stage-d/step-4/landing";
import StageDInPrincipleApproval from "./pages/application-coo-stage-d/step-2/landing";
import StageDValuationFeeSuccess from "./pages/application-coo-stage-d/valuation-fee/valuation-fee-success";
import StageDValuationFeeUnsuccessful from "./pages/application-coo-stage-d/valuation-fee/valuation-fee-unsuccessful";
import StageEPage from "./pages/application-coo-stage-e";
import StageAViewApplication from "./pages/application-coo/view-application";
import StageASuccess from "./pages/application-coo/application-success";
import CancelApplicationPayment from "./pages/application-coo/cancel-application-payment";
import ApplicationCancelled from "./pages/application-coo/application-cancelled";
import Logout from "./pages/logout";
import Page404 from "./pages/404";
import Page500 from "./pages/500";
import PendingItemsPage from "./pages/application-coo/pending-items";
import Auth from "./pages/auth";
import NormalApiLogin from "./pages/authentication/normal-login";
import { setBackTriggered } from "./redux/slices/back-triggered-slice";
import EstimateFees from "./pages/estimate-fees";
import { SolicitorDashboard } from "./pages/solicitor/dashboard/solicitor-dashboard";
import { SolicitorCaseSummary } from "./pages/solicitor/case-details/solicitor-case-details";
import StageDProposedOwnerFinancialPlanPage from "./pages/application-coo-stage-d/step-5/ProposedFinancialPlan";
import StageDCurrentOwnerFinancialPlanPage from "./pages/application-coo-stage-d/step-4/CurrentOwnerFinancialPlan";
import { SingpassTypeEnum } from "./models/enum/singpass-type.enum";
import StageECpfLanding from "./pages/application-coo-stage-e/cpf-landing/landing";
import StageECpfOverview from "./pages/application-coo-stage-e/cpf-step-1/landing";
import StageERequestUseOfCpf from "./pages/application-coo-stage-e/cpf-step-2/landing";
import StageEHomeProtectionScheme from "./pages/application-coo-stage-e/cpf-step-3/landing";
import StageESubmitOverview from "./pages/application-coo-stage-e/cpf-submit-overview/landing";
import StageENotifyCoApplicant from "./pages/application-coo-stage-e/notify-co-applicant/notify-co-applicant";
import LogoutPublic from "./pages/logout-public";
import CompletedApplication from "./pages/application-coo-stage-e/completed-application";
import StageEConfirmationDeclaration from "./pages/application-coo-stage-e/cpf-step-4/landing";
import SignifyRedirect from "./pages/signify-redirect";
import HeadManager from "./components/header/headmanager";
import SearchSG from "./pages/searchsg";
import MaintenancePage from "./pages/maintenence";

// WOGAA script URL based on environment
// Production: assets.wogaa.sg | Staging/Dev: assets.dcube.cloud
const getWogaaScriptUrl = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  
  // Only use production WOGAA for the actual production domain
  if (origin === 'https://services3.hdb.gov.sg') {
    return 'https://assets.wogaa.sg/scripts/wogaa.js';
  }
  
  // All other environments (dev, staging, localhost) use staging WOGAA
  return 'https://assets.dcube.cloud/scripts/wogaa.js';
};

function App() {
  const BASE_PATH = "/transfer";

  const isAuthenticated = useAppSelector( (state) => state.auth.AuthenticationSlice.isAuthenticated);
  const singpassType = useAppSelector( (state) => state.auth.AuthenticationSlice.singPassType);
  const completedStageA = useAppSelector(state => state.app.CompletedStepsSlice.stage_a);
  const successPageStageA = useAppSelector(state => state.app.CompletedStepsSlice.step_a_success);
  const startCooApplication = useAppSelector(state => state.route.RouteManagementSlice.startCooApplication);
  const startAddressSelection = useAppSelector(state => state.route.RouteManagementSlice.startAddressSelection);
  const startStageD = useAppSelector(state => state.route.RouteManagementSlice.startStageD);
  const startStageE = useAppSelector(state => state.route.RouteManagementSlice.startStageE);
  const applicationStatus = useAppSelector(state => state.app.ApplicationStageDSlice.application_status);
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const AppDispatch = useAppDispatch();
  const wogaaScriptLoaded = useRef(false);

  // Load WOGAA script once on mount - moved to useEffect to prevent duplicate loading on re-renders
  useEffect(() => {
    const wogaaScriptUrl = getWogaaScriptUrl();
    
    // Check if script already exists or has been loaded
    if (wogaaScriptLoaded.current || document.querySelector(`script[src*="wogaa.js"]`)) {
      return;
    }

    const wogaa_script = document.createElement('script');
    wogaa_script.setAttribute('src', wogaaScriptUrl);
    wogaa_script.async = true;
    document.head.appendChild(wogaa_script);
    wogaaScriptLoaded.current = true;
    
    console.debug(`[WOGAA] Loaded script from: ${wogaaScriptUrl}`);

    // Update document title for non-production environments
    if (process.env.NODE_ENV !== "production" || 
        document.location.origin === "https://services3-dev.hdb.gov.sg" || 
        document.location.origin === "https://services3-stg.hdb.gov.sg") {
      if (!document.title.includes(config.version)) {
        document.title = `${document.title} - ${config.version}`;
      }
    }
  }, []);

  function withHeadManager(routes: any[]) {
  return [
    {
      element: <HeadManager />, // ← in‐router context, so useMatches works
      children: routes,
    },
  ];
}


  const publicRouter = createBrowserRouter(
    withHeadManager([
      {
        path: "/",
        element: <NormalApiLogin />,
        errorElement: <Navigate to="/" />,
      },
      {
        path: "/dashboard",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/search",
        element: <SearchSG />,
        errorElement: <Page404 />,
      },
      {
        path: "/solicitor",
        element: <NormalApiLogin />,
        errorElement: <Navigate to="/solicitor" />,
      },
      {
        path: "/logout",
        element: <LogoutPublic />,
        errorElement: <Page404 />,
      },
      {
        path: "/404",
        element: <Page404 />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/500",
        element: <Page500 />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/signify-redirect",
        element: <SignifyRedirect />,
        errorElement: <ErrorPage />,
      },
      {
        path: "/maintenance",
        element: <MaintenancePage />,
        errorElement: <ErrorPage />,
      },
      ...(process.env.REACT_APP_SHOW_ESTIMATED_FEES
        ? [
            {
              path: "/estimate-fees-from-eservice",
              element: <EstimateFees fromTransfer={false} />,
              errorElement: <Page404 />,
            },
          ]
        : []),
    ]),
    {
      basename: BASE_PATH,
    }
  );

  const stageARouters = useMemo(()=> [
    {
      path: "/application-coo/stage-a/declaration",
      element: <StartApplicationCOO />,
    },
    {
      path: "/application-coo/stage-a/eligibility",
      element: <ApplicationCOOStep1 />,
    },
    {
      path: "/application-coo/stage-a/financing",
      element: <ApplicationCOOStep2 />,
    },
    {
      path: "/application-coo/stage-a/conveyancing",
      element: <ApplicationCOOStep3 />,
    },
    {
      path: "/application-coo/stage-a/upload-documents",
      element: <ApplicationCOOStep4 />,
    },
    {
      path: "/application-coo/stage-a/summary",
      element: <ApplicationCOOStep5 />,
    },
    {
      path: "/application-coo/stage-a/view-application",
      element: <StageAViewApplication />
    }
  ],[]);


  const stageACompleted = useMemo(()=> [
    {
      path: "/application-coo/stage-a/declaration",
      element: <StartApplicationCOO />,
    },
    {
      path: "/application-coo/stage-a/consent",
      element: <ConsentPage stage="A" />,
    },

  ],[]);

  const stageASuccess = useMemo(()=>[
    {
      path: "/application-coo/stage-a/success",
      element: <StageASuccess />
    }
  ],[])


  const manageStageARouters = useMemo(()=>{
    let stageA:any = completedStageA

    const debugRoute = true;
    const allFieldsTrue = reduce(keys(stageA),(result, key) => result && stageA[key.toString()],true); //When step

    let routes:any[] = [];
    if(!startCooApplication){
      return [];
    }
    if(!allFieldsTrue || debugRoute){
      const processedRouters = !applicationStatus || applicationStatus === 'SAVE_DRAFT' ? stageARouters : stageARouters.filter(item => item.path === "/application-coo/stage-a/view-application");
      routes = [...routes, ...processedRouters];
    }
    if(allFieldsTrue || debugRoute){
      routes = [...routes, ...stageACompleted];
    }
    if(successPageStageA || debugRoute){
      routes = [...routes, ...stageASuccess];
    }
    return routes
  }, [completedStageA, startCooApplication, successPageStageA, stageARouters, stageACompleted, stageASuccess, applicationStatus])


  const manageAddressSelectionRoute = useMemo(()=>{
    if(!startAddressSelection){
      return [];
    }
    return [
      {
        path: "/application-coo/property-selection",
        element: <PropertySelection />,
      }
    ]

  },[ startAddressSelection ])





  const manageStateDRouters = useMemo(()=>{
    //Open stage D route when at Stage E
    console.log("DEBUG startStageD || startStageE",startStageD, startStageE);
    if(startStageD || startStageE){
      return [
        {
          path: "/application-coo/stage-d/hdb-review",
          element: <StageDApplicationReview />,
        },
        {
          path: "/application-coo/stage-d/in-principle-approval",
          element: <StageDInPrincipleApproval />,
        },
        {
          path: "/application-coo/stage-d/review-financial-plan",
          element: <StageDFinancialReview />,
        }
      ]
    }
    return [];

  },[startStageD, startStageE])

  const manageStateERouters = useMemo(()=>{
    if(!startStageE){
      return [];
    }

    return [
      {
        path: "/application-coo/stage-e/prepare-for-completion",
        element: <StageEPage />,
      },
      {
        path: "/application-coo/stage-e/cpf-e-service",
        element: <StageECpfLanding />
      },
      {
        path: "/application-coo/stage-e/overview",
        element: <StageECpfOverview />
      },
      {
        path: "/application-coo/stage-e/request-use-of-cpf",
        element: <StageERequestUseOfCpf />
      },
      {
        path: "/application-coo/stage-e/home-protection-scheme",
        element: <StageEHomeProtectionScheme />
      },
      {
        path: "/application-coo/stage-e/confirmation-declaration",
        element: <StageEConfirmationDeclaration />
      },
      {
        path: "/application-coo/stage-e/submit-overview",
        element: <StageESubmitOverview />
      },
      {
        path: "/application-coo/stage-e/notify-co-applicant",
        element: <StageENotifyCoApplicant />
      },
      {
        path: "/application-coo/stage-e/completed-application",
        element: <CompletedApplication />
      }
    ];
  },[startStageE, applicationStatus])


  const router = [
    {
      path: "/",
      element: <Auth />,
      errorElement: <Page404 />,
    },
    {
      // Fallback route for /dashboard when singPassType is not yet loaded
      // This prevents 404 during the race condition between isAuthenticated and singPassType
      path: "/dashboard",
      element: <Auth />,
      errorElement: <Page404 />,
    },
    {
      path: "/solicitor",
      element: <Auth />,
      errorElement: <Page404 />,
    },
    {
      path: "/logout",
      element: <Logout />,
      errorElement: <Page404 />,
    },
    {
      path: "/404",
      element: <Page404 />,
      errorElement: <Page404 />,
    },
    {
      path: "/500",
      element: <Page500 />,
      errorElement: <Page500 />,
    },
    {
      path: "/signify-redirect",
      element: <SignifyRedirect />,
      errorElement: <ErrorPage />,
    },
  ];

  const singpass_router = [
    {
      path: "/dashboard",
      element: <Root />,
      errorElement: <Page404 />,
    },
    {
      path: "/search",
      element: <SearchSG />,
      errorElement: <Page404 />,
    },
    {
      path: "/error",
      element: <Page404 />,
      errorElement: <Page404 />,
    },
    {
      path: "/application-coo",
      element: <StagesProcess />,
    },
    {
      path: "/application-coo/cancel-application-payment",
      element: <CancelApplicationPayment />,
    },
    {
      path: "/application-coo/stage-b/consent",
      element: <ConsentPage stage="B" />,
    },
    {
      path: "/application-coo/stage-b/review",
      element: <OthersReview />,
    },
    {
      path: "/application-coo/stage-c/payment",
      element: <StageCPaymentLanding />,
    },
    {
      path: "/application-coo/stage-c/payment/successful",
      element: <StageCSuccessful />,
    },
    {
      path: "/application-coo/stage-c/payment/unsuccessful",
      element: <StageCUnsuccessful />,
    },
    {
      path: "/application-coo/stage-d/valuation-fee/success",
      element: <StageDValuationFeeSuccess />,
    },
    {
      path: "/application-coo/stage-d/valuation-fee/unsuccessful",
      element: <StageDValuationFeeUnsuccessful />,
    },
    {
      path: "/application-coo/application-cancelled",
      element: <ApplicationCancelled />,
    },
    {
      path: "/application-coo/pending-items",
      element: <PendingItemsPage />,
    },
    {
      path: "/application-coo/stage-d/review-financial-plan-current",
      element: <StageDCurrentOwnerFinancialPlanPage />,
    },
    {
      path: "/application-coo/stage-d/review-financial-plan-proposed",
      element: <StageDProposedOwnerFinancialPlanPage />,
    },
    ...(process.env.REACT_APP_SHOW_ESTIMATED_FEES
      ? [
          {
            path: "/estimate-fees",
            element: <EstimateFees fromTransfer={true} />,
            errorElement: <Page404 />,
          },
          {
            path: "/estimate-fees-from-eservice",
            element: <EstimateFees fromTransfer={false} />,
            errorElement: <Page404 />,
          },
        ]
      : []),
  ]


  const solicitor_router = [
    {
      path: "/solicitor/dashboard",
      element: <SolicitorDashboard />,
      handle: {
        title: "Solicitor · Dashboard",
        description: "View your cases, manage your solicitor account, and access important information and tools related to your ongoing and past cases.",
      }
    },
    {
      path: "solicitor/case",
      element: <SolicitorCaseSummary />,
      handle: {
        title: "Solicitor · Case",
        description: "View and manage the details of a specific case, including documents, status updates, and communication with relevant parties.",
      }
    },
  ]

  const authRouter = createBrowserRouter(
    withHeadManager([
      // Specific routes first (they override fallbacks in router)
      ...(singpassType === SingpassTypeEnum.singpass ? singpass_router : []),
      ...(singpassType === SingpassTypeEnum.corppass ? solicitor_router : []),
      // Base router with fallbacks comes after specific routes
      ...router,
      ...manageStageARouters,
      ...manageAddressSelectionRoute,
      ...manageStateDRouters,
      ...manageStateERouters,
    ]),
    {
      basename: BASE_PATH,
    }
  );

  // only for dev
  // const path = window.location.pathname;
  // useEffect(() => {
  //   let updatingPath = path === "/transfer" ? "/" : path.replace("/transfer" , "");
  //   if(authRouter.routes.every(item => item.path !== updatingPath)){
  //       window.location.href = "/transfer/404";
  //   }
  //   if (path === '/') {
  //     window.location.href = '/transfer';
  //   }

  //   AppDispatch(setLeavePageModalOpen(false));
  //   AppDispatch(setNavigatingValue(undefined));
  //   return () => {
  //      AppDispatch(setPreviousPageUrl(updatingPath));
  //   }
  // }, [path]);
  useEffect(() => {
    const stageAApplicationRoutes: string[] = [
      "/application-coo/stage-a/eligibility",
      "/application-coo/stage-a/financing",
      "/application-coo/stage-a/conveyancing",
      "/application-coo/stage-a/upload-documents",
      "/application-coo/stage-a/summary",
    ];
    //if the user leave stage A application page, set back trigger to false
    //so that next time when enter application again, the back modal wont be triggered directly
    if(stageAApplicationRoutes.every(item => !window.location.href.includes(item))){
      AppDispatch(setBackTriggered(false));
    }
  }, [window.location.href])
  return (
    <React.StrictMode>
      <HeaderV2 isHidden={isHidden} setIsHidden={setIsHidden} />
      {!isAuthenticated && <RouterProvider router={publicRouter} />}
      {isAuthenticated && <RouterProvider router={authRouter} />}
      <FooterV2 />
    </React.StrictMode>
  );
}

export default App;
